import type { TermCards } from "../../ontology";

/**
 * How a link in a Run's text shows (Johannes, 4 Nov: "we should not assume things that aren't
 * there yet"): `term`, a term the data model knows at the Run's version, opened in DMW's own
 * term card; `unvouched`, an address in the model's own namespaces that it doesn't know, which
 * nothing says leads anywhere, shown as plain text; `web`, any other address, an ordinary link,
 * written bare or not (the orchestrator's b89 decision, 4 Nov 19:10, relaxing the provisional rule
 * of 16:21 now that the model declares its namespaces, a project's own extension too).
 */
export type RunLinkKind = "term" | "unvouched" | "web";

/** The model's namespaces, from its terms' IRIs: read once per set of term cards. */
const namespacesOf = new WeakMap<TermCards, ReadonlySet<string>>();

/** An IRI's namespace: everything up to its last `#` or `/`. */
function namespaceOf(iri: string): string {
  return iri.slice(0, Math.max(iri.lastIndexOf("#"), iri.lastIndexOf("/")) + 1);
}

function modelNamespaces(cards: TermCards): ReadonlySet<string> {
  let namespaces = namespacesOf.get(cards);
  if (namespaces === undefined) {
    namespaces = new Set([...cards.keys()].map(namespaceOf));
    namespacesOf.set(cards, namespaces);
  }
  return namespaces;
}

/**
 * `cards`: the data model's terms at the Run's version; `null` while they load or when they
 * can't be read, when nothing is vouched for (Sol round 1, S2). `declared`: the namespaces the
 * model's modules declare at that version (`declaredNamespaces`), beside those of its terms. An
 * anchor in the text itself, such as a heading's own, is always a link.
 */
export function runLinkKind(href: string, cards: TermCards | null, declared: ReadonlySet<string>): RunLinkKind {
  if (href.startsWith("#")) return "web";
  if (cards === null) return "unvouched";
  if (cards.has(href)) return "term";
  const namespace = namespaceOf(href);
  return namespace !== "" && (declared.has(namespace) || modelNamespaces(cards).has(namespace)) ? "unvouched" : "web";
}
